import { makeAutoObservable, runInAction } from 'mobx'
import type { AxiosResponse } from 'axios'
import { logger, prepareSearchPhoneNumber } from 'shared/lib'
import { type Integration, IntegrationKey, IntegrationsApi } from 'entities/Integrations'
import { PhoneApi } from 'entities/Phone'
import { type IResponseIntegrationNumber } from 'entities/Phone/api/types'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import {
  type AvailableIntegrationKey,
  availableIntegrationToAddNumber,
} from 'pages/settings/pages/numbers/pages/AddNumberFromApp/lib/constants'

export class SelectAppAndNumbersStore {
  private _integrationList: Integration[] = []
  private _selectedNumberVendorId: number | null = null
  private _selectedIntegrationKey: AvailableIntegrationKey | null = null
  private _supportLinkCache: Partial<Record<AvailableIntegrationKey, string>> = {}

  private _isNumbersLoading = false
  private _numberList: IResponseIntegrationNumber[] = []
  private _selectedNumberId: number | null = null
  private _numberSearch = ''

  private readonly supportLinkLoaders: Record<
    AvailableIntegrationKey,
    () => Promise<AxiosResponse<string>>
  > = {
    ['aircall']: () => IntegrationsApi.getAirCallIntegrationLink(),
  }

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this._init()
  }

  _init = async () => {
    await this._entities.integrationsStore.fetchIntegrations()
    runInAction(() => {
      this._integrationList = this._entities.integrationsStore.getIntegrations([
        ...availableIntegrationToAddNumber,
      ])
    })
  }

  fetchIntegrationNumbers = async (integrationKey: AvailableIntegrationKey) => {
    runInAction(() => {
      this._isNumbersLoading = true
    })
    try {
      switch (integrationKey) {
        case IntegrationKey.aircall: {
          const supportLinkPromise = this._supportLinkCache[integrationKey]
            ? Promise.resolve(this._supportLinkCache[integrationKey])
            : this.supportLinkLoaders[integrationKey]().then((response) => response.data)

          const [
            {
              data: { data, vendor },
            },
            supportLink,
          ] = await Promise.all([
            PhoneApi.getAircallNumbers({ page: 1, limit: 500 }),
            supportLinkPromise,
          ])

          runInAction(() => {
            this._numberList = data
            this._selectedNumberVendorId = vendor.id
            this._supportLinkCache[integrationKey] = supportLink ?? ''
          })
        }
      }
    } catch (error) {
      logger.error(error)
    } finally {
      runInAction(() => {
        this._isNumbersLoading = false
      })
    }
  }

  get selectedIntegrationSupportLink() {
    const key = this._selectedIntegrationKey
    if (!key) return ''
    return this._supportLinkCache[key]
  }

  setSelectedIntegrationKey = (integrationKey: AvailableIntegrationKey) => {
    this._selectedIntegrationKey = integrationKey
    this._selectedNumberId = null
    this._numberList = []
    this._numberSearch = ''

    this.fetchIntegrationNumbers(integrationKey)
  }

  setSelectedNumberId = (numberId: string) => {
    this._selectedNumberId = Number(numberId)
  }

  setNumberSearch = (search: string) => {
    this._numberSearch = search
  }

  filterNumberBySearch = (number: IResponseIntegrationNumber) => {
    const normalizedSearch = prepareSearchPhoneNumber(this._numberSearch)
    const normalizedNumber = prepareSearchPhoneNumber(number.formatted_number ?? '')

    return normalizedNumber.includes(normalizedSearch)
  }

  reset = () => {
    this._selectedIntegrationKey = null
    this._selectedNumberId = null
    this._selectedNumberVendorId = null
    this._numberSearch = ''
  }

  get integrationList() {
    return this._integrationList
  }

  get selectedIntegrationKey() {
    return this._selectedIntegrationKey
  }

  get selectedNumberVendorId() {
    return this._selectedNumberVendorId
  }

  get isIntegrationSelected() {
    return !!this._selectedIntegrationKey
  }

  get isNextDisabled() {
    return !this._selectedNumberId
  }

  get activeNumbersList() {
    return this._numberList.filter(
      (item) => !item.assigned && item.active && this.filterNumberBySearch(item)
    )
  }

  get inactiveNumbersList() {
    return this._numberList.filter(
      (item) => !item.assigned && !item.active && this.filterNumberBySearch(item)
    )
  }

  get selectedNumberId() {
    return this._selectedNumberId
  }

  get isNumbersLoading() {
    return this._isNumbersLoading
  }

  get selectedIntegration() {
    return this._integrationList.find(
      (integration) => integration.key === this._selectedIntegrationKey
    )
  }

  get selectedNumber() {
    return this._numberList.find((number) => number.id === this._selectedNumberId)
  }

  get numberSearch() {
    return this._numberSearch
  }
}
